import { ReactNode } from "react";

type Status = "ok" | "warn" | "crit" | "unknown";

interface Props {
  value: number;
  thresholds: { green: number; yellow: number; red: number };
  /** if true, lower is better (e.g. latency); else higher is better */
  inverse?: boolean;
  label?: string;
  unit?: string;
  children?: ReactNode;
}

function classify(value: number, t: Props["thresholds"], inverse: boolean): Status {
  if (Number.isNaN(value)) return "unknown";
  if (inverse) {
    if (value <= t.green) return "ok";
    if (value <= t.yellow) return "warn";
    return "crit";
  }
  if (value >= t.green) return "ok";
  if (value >= t.yellow) return "warn";
  return "crit";
}

const STATUS_CLASS: Record<Status, string> = {
  ok: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  warn: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  crit: "bg-destructive/15 text-destructive border-destructive/30",
  unknown: "bg-muted text-muted-foreground border-border",
};

const STATUS_LABEL: Record<Status, string> = {
  ok: "OK", warn: "Atenção", crit: "Crítico", unknown: "—",
};

/** UI-09: status indicator with explicit thresholds (green/yellow/red). */
export function HealthIndicator({ value, thresholds, inverse = false, label, unit, children }: Props) {
  const status = classify(value, thresholds, inverse);
  return (
    <div className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs ${STATUS_CLASS[status]}`}>
      <span className="size-2 rounded-full bg-current" />
      {label && <span className="font-medium">{label}:</span>}
      <span>{Number.isNaN(value) ? "—" : value.toLocaleString("pt-BR")}{unit ?? ""}</span>
      <span className="opacity-70">· {STATUS_LABEL[status]}</span>
      {children}
    </div>
  );
}
